'use client';

import AnsweringServiceVsGreet from '@/components/AnsweringServiceVsGreet';
import LandingLayout from '@/components/LandingLayout';
import { plumbingFaqs } from '@/data/industryFaqs';
import { useAuth } from '@/contexts/AuthContextNext';
import { trackButtonClick, trackPageView, trackPhoneClick } from '@/lib/analytics';
import { PUBLIC_SALE_STARTING_PRICE } from '@/types/pricing';
import {
  CalendarIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  ClockIcon,
  DocumentTextIcon,
  PhoneIcon,
  SparklesIcon,
  StarIcon,
  WrenchScrewdriverIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';
import Link from 'next/link';
import { useEffect, useState } from 'react';

export default function PlumbingLanding() {
  useEffect(() => {
    trackPageView('Plumbing Landing', undefined, { funnel_step: 'industry_landing' });
  }, []);

  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const { isAuthenticated } = useAuth();

  const painPoints = [
    {
      problem: "Emergency calls at 2 AM? You miss them.",
      impact: "$500-2,000 per missed emergency call"
    },
    {
      problem: "Answering 'How much for a drain cleaning?' 40+ times daily",
      impact: "6-10 hours weekly on the same questions"
    },
    {
      problem: "Phone ringing while elbow-deep in a crawlspace",
      impact: "Miss calls or interrupt jobs mid-flow"
    },
    {
      problem: "Seasonal volume spikes (winter burst pipes, summer AC lines)",
      impact: "Can't scale your team fast enough"
    }
  ];

  const plumbingBenefits = [
    {
      title: "Never Miss a 24/7 Emergency Call",
      description: "Your AI answers burst pipes, handles scheduling, pre-qualifies repairs, and catches high-paying emergency work 24/7-while you focus on actual plumbing.",
    },
    {
      title: "Save 8-10 Hours Weekly",
      description: "Stop repeating pricing for drain cleaning, toilet repairs, and water heater work. AI handles the script perfectly every time.",
      icon: ClockIcon,
      stat: "8-10 hrs"
    },
    {
      title: "Auto-Schedule Service Calls",
      description: "Customers book their own appointments directly into your calendar. No back-and-forth. Reduces scheduling time by 90%.",
      icon: CalendarIcon,
      stat: "Auto"
    },
    {
      title: "Capture High-Margin Emergency Work",
      description: "Winter pipe bursts and summer line replacements are your highest-paying calls. Never lose that revenue to slow response times.",
      icon: WrenchScrewdriverIcon,
      stat: "100%"
    },
    {
      title: "Handle Seasonal Spikes",
      description: "Winter has 5x call volume? No problem. The AI handles everything the same way. You go from 'drowning in calls' to 'we answered every single one.'",
      icon: SparklesIcon,
      stat: "Unlimited"
    },
    {
      title: "Smart Qualification",
      description: "AI asks diagnostic questions while you're on other jobs. You get detailed summaries before you call the customer back.",
      icon: DocumentTextIcon,
      stat: "Pre-diagnosed"
    }
  ];

  const scenarios = [
    {
      title: "The 2 AM Burst Pipe Emergency",
      problem: "Tuesday 2 AM: Homeowner's pipes burst, water pouring into basement. Calls your number, gets voicemail. By morning, they've called 3 competitors and one is already on the way.",
      solution: "With Greet: Your AI answers instantly, assesses the emergency, confirms it's a burst pipe, gets the address and nearest water shutoff, and wakes you up immediately. You're first on scene."
    },
    {
      title: "Spring Renewal Season (March-May)",
      problem: "Spring brings 100+ calls/week for drain cleaning, leak repairs, and seasonal maintenance. You can handle maybe 30 personally. Missing 70+ calls means missing $5,000-10,000/week.",
      solution: "With Greet: Every call answered instantly. Routine questions about pricing and scheduling are handled by AI. You only get interrupted for complex jobs needing your expertise. Calendar fills automatically."
    },
    {
      title: "The Diagnostic Call You Wished You'd Caught",
      problem: "Customer calls about 'slow drain.' Your competitor answers. Upsells them on full line cleaning ($800 job). You get a referral voicemail the next day.",
      solution: "With Greet: AI asks about which drain, how long it's been slow, other symptoms. Recognizes pattern as full line issue, books you for tomorrow at $800 rate. You show up already knowing what's needed."
    }
  ];

  const testimonials = [
    {
      name: "Joe Martinez",
      business: "Martinez Emergency Plumbing",
      stars: 5,
      quote: "As an emergency plumber, those middle-of-the-night calls are everything. Greet catches every single 2 AM emergency now. I'm always first on scene. That alone is worth $2,000+/month in extra work.",
      metric: "Never misses a call"
    },
    {
      name: "Sarah Kim",
      business: "Kim's 24/7 Plumbing",
      stars: 5,
      quote: "Winter is chaos-burst pipes everywhere. Greet handles 80% of routine questions so I can focus on actual emergency responses. My team isn't burned out, and we're answering 100% of calls.",
      metric: "$8K/month in winter overflow"
    },
    {
      name: "Mike Thompson",
      business: "Thompson Drain & Sewer",
      stars: 5,
      quote: "The AI pre-qualifies drain jobs perfectly. I know if it's a simple clean or a full line replacement before I even call back. Saves me hours diagnosing on the phone and lets me quote faster.",
      metric: "50% more jobs closed"
    }
  ];

  return (
    <LandingLayout>
      {/* Hero */}
      <section className="relative bg-gradient-to-br from-blue-900 via-slate-900 to-blue-900 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 via-cyan-500/10 to-blue-500/10"></div>
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] rounded-full bg-gradient-to-r from-blue-500/20 to-cyan-500/20 blur-3xl"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <div className="max-w-4xl">
            <div className="mb-6 inline-flex items-center gap-3 rounded-full bg-blue-500/20 border border-blue-400/30 px-4 py-2 text-sm font-semibold text-blue-100">
              <span className="h-2 w-2 rounded-full bg-blue-400"></span>
              Answering service for plumbers · from {PUBLIC_SALE_STARTING_PRICE}
            </div>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
              Never Miss Another 2 AM Emergency Call
            </h1>
            <p className="text-xl text-blue-100 mb-4 max-w-2xl leading-relaxed">
              Your AI plumbing virtual receptionist answers burst pipes, books jobs, and catches high-paying emergency work 24/7-for a fraction of a $200–$400/mo answering service.
            </p>
            <p className="text-lg text-blue-200/90 mb-8">
              Flat-rate from {PUBLIC_SALE_STARTING_PRICE} · No per-minute fees · 7-day free trial
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                href="/try"
                onClick={() => trackButtonClick('try_demo', 'plumbing_hero')}
                className="bg-blue-500 hover:bg-blue-600 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-colors inline-flex items-center justify-center"
              >
                <PhoneIcon className="h-5 w-5 mr-2" />
                Hear it answer in 60 seconds
              </Link>
              <Link
                href={isAuthenticated ? '/app' : '/register'}
                onClick={() => trackButtonClick(isAuthenticated ? 'go_to_dashboard' : 'start_free_trial', 'plumbing_hero')}
                className="bg-white/10 border border-white text-white hover:bg-white/20 px-8 py-4 rounded-lg font-semibold text-lg transition-colors inline-flex items-center justify-center"
              >
                {isAuthenticated ? 'Go to Dashboard' : 'Start Free Trial'}
              </Link>
            </div>
            <p className="mt-4 text-sm text-blue-200/80">
              Or call a live demo:{' '}
              <a
                href="tel:+14158773166"
                onClick={() => trackPhoneClick('+14158773166', 'plumbing_demo')}
                className="underline hover:text-white"
              >
                (415) 877-3166
              </a>
            </p>
          </div>
        </div>
      </section>

      {/* Pain Points Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              The Plumber's Call Management Nightmare
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Every missed call is money leaving your business. Every answered call you handle personally is time away from profitable work.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            {painPoints.map((point, index) => (
              <div key={index} className="border-l-4 border-red-500 pl-6 py-4">
                <div className="flex items-start gap-4">
                  <XCircleIcon className="h-6 w-6 text-red-500 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-bold text-gray-900 text-lg mb-2">{point.problem}</h3>
                    <p className="text-red-600 font-semibold">{point.impact}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Built for Plumbing: Here's What Changes
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Your AI receptionist handles emergencies, scheduling, and diagnostics while you do what you do best-fix problems and make money.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {plumbingBenefits.map((benefit, index) => (
              <div key={index} className="bg-white rounded-2xl border border-gray-100 p-8 hover:shadow-xl transition-all duration-300 hover:-translate-y-2 group">
                {benefit.icon && (
                  <div className="w-16 h-16 rounded-xl bg-gradient-to-r from-blue-500 to-cyan-500 flex items-center justify-center mb-6 shadow-lg transition-transform duration-300 group-hover:scale-110">
                    <benefit.icon className="h-8 w-8 text-white transition-transform duration-300 group-hover:scale-125" />
                  </div>
                )}
                <div className="inline-block px-4 py-1 bg-blue-100 rounded-full mb-4 transition-all duration-300 group-hover:bg-blue-200 group-hover:scale-105">
                  <span className="text-2xl font-bold text-blue-900 transition-colors duration-300 group-hover:text-blue-900">{benefit.stat}</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3 transition-colors duration-300 group-hover:text-blue-600">{benefit.title}</h3>
                <p className="text-gray-600 leading-relaxed transition-colors duration-300 group-hover:text-gray-700">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Real Scenarios */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              How Plumbers Actually Use Greet
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Real scenarios. Real money. Real solutions.
            </p>
          </div>
          
          <div className="space-y-8">
            {scenarios.map((scenario, index) => (
              <div key={index} className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-2xl p-8 border border-blue-200">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{scenario.title}</h3>
                
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <div className="flex items-start gap-3 mb-3">
                      <XCircleIcon className="h-6 w-6 text-red-500 flex-shrink-0 mt-1" />
                      <span className="font-semibold text-gray-900">Without Greet</span>
                    </div>
                    <p className="text-gray-700 leading-relaxed">{scenario.problem}</p>
                  </div>
                  
                  <div>
                    <div className="flex items-start gap-3 mb-3">
                      <CheckCircleIcon className="h-6 w-6 text-green-500 flex-shrink-0 mt-1" />
                      <span className="font-semibold text-gray-900">With Greet</span>
                    </div>
                    <p className="text-gray-700 leading-relaxed">{scenario.solution}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Trusted by Plumbers
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Real plumbers. Real results. Real revenue impact.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 group">
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.stars)].map((_, i) => (
                    <StarIcon key={i} className="h-5 w-5 text-yellow-400 fill-current transition-colors duration-300 group-hover:text-yellow-500" />
                  ))}
                </div>
                <p className="text-gray-700 mb-4 leading-relaxed transition-colors duration-300 group-hover:text-gray-900">"{testimonial.quote}"</p>
                <div className="bg-blue-100 rounded-lg px-4 py-3 mb-4 transition-colors duration-300 group-hover:bg-blue-200">
                  <p className="text-blue-900 font-semibold text-sm">{testimonial.metric}</p>
                </div>
                <div>
                  <div className="font-semibold text-gray-900 transition-colors duration-300 group-hover:text-blue-600">{testimonial.name}</div>
                  <div className="text-gray-600 text-sm transition-colors duration-300 group-hover:text-gray-700">{testimonial.business}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <AnsweringServiceVsGreet
        industryLabel="Plumbers"
        analyticsContext="plumbing_comparison"
        accent="blue"
      />

      <section className="pb-8 bg-white">
        <div className="max-w-5xl mx-auto px-4 text-center">
          <p className="text-gray-600">
            Shopping traditional phone coverage?{' '}
            <Link href="/answering-service-for-plumbers" className="text-blue-700 font-semibold hover:text-blue-900">
              See our answering service for plumbers comparison
            </Link>
            {' '}or{' '}
            <Link href="/ai-receptionist-pricing" className="text-blue-700 font-semibold hover:text-blue-900">
              AI receptionist pricing
            </Link>
            .
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Questions from Plumbers
            </h2>
            <p className="text-xl text-gray-600">
              Everything you need to know about AI call handling for plumbing.
            </p>
          </div>
          
          <div className="space-y-4">
            {plumbingFaqs.map((faq, index) => (
              <div key={index} className="border border-gray-200 rounded-lg overflow-hidden">
                <button
                  className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-gray-50 transition-colors"
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                >
                  <span className="font-semibold text-gray-900">{faq.question}</span>
                  <ChevronDownIcon 
                    className={`h-5 w-5 text-gray-500 transform transition-transform ${
                      openFaq === index ? 'rotate-180' : ''
                    }`} 
                  />
                </button>
                <div className={`px-6 text-gray-600 border-t border-gray-200 bg-gray-50 transition-all duration-300 ${
                  openFaq === index ? 'pb-4 max-h-96' : 'pb-0 max-h-0'
                } overflow-hidden`}>
                  {faq.answer}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-gradient-to-r from-blue-600 to-cyan-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Stop Missing Emergency Calls
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Your AI receptionist is ready to answer 24/7. Start your free trial today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              href="/try"
              onClick={() => trackButtonClick('try_demo', 'plumbing_cta')}
              className="bg-white text-blue-600 hover:bg-gray-50 px-8 py-4 rounded-lg font-semibold text-lg transition-colors inline-flex items-center justify-center"
            >
              <PhoneIcon className="h-5 w-5 mr-2" />
              Hear it answer in 60 seconds
            </Link>
            <Link
              href={isAuthenticated ? '/app' : '/register'}
              onClick={() => trackButtonClick(isAuthenticated ? 'go_to_dashboard' : 'start_free_trial', 'plumbing_cta')}
              className="bg-transparent border border-white text-white hover:bg-white/10 px-8 py-4 rounded-lg font-semibold text-lg transition-colors inline-flex items-center justify-center"
            >
              {isAuthenticated ? 'Go to Dashboard' : 'Start Free Trial'}
            </Link>
          </div>
        </div>
      </section>
    </LandingLayout>
  );
} 
